import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// local imports && assets
import successImage from 'assets/images/success-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import useDidMountEffect from 'utils/useDidMountEffect'
import AuthUtil from 'utils/AuthUtil'
import DateUtil from 'utils/DateUtil'
import { IFilteredPoolVehicleList, IPoolVehicleListReq } from 'types/PoolVehicleType'
import useGetPoolVehicleList from '../hooks/use-get-pool-vehicles-list'
import useGetPoolVehicleLocationList from '../hooks/use-get-pool-vehicles-locations'
import usePoolVehicleResModal from '../hooks/use-pool-vehicles-res-modal'
import ReservationModalWrapper from '../reservation-scheduler/ReservationModal'
import PoolVehicleSearch from './PoolVehicleSearch'
import PoolVehicleCard from './PoolVehicleCard'
import PoolVehicleFilterForm from './PoolVehicleFilterForm'
import Loader from 'components/ui/Loader'
import NoContent from 'components/ui/NoContent'
import NotificationDialog from 'components/ui/NotificationDialog'
import { Divider, FilterButton, FormLayoutWrapper, ResetButton, RoomCardList } from './styled'

/* filtered date - default */
const TODAY = DateUtil.getStartOfDay(new Date())

export default function PoolVehicleList() {
  const history = useHistory()
  const { t } = useTranslation()
  const [locationIds, setLocationIds] = useState<number[] | []>([])

  /* Get Current logged employee infos - (location id) */
  const { data: locationList, isLoading, fuelTypes, gearTypes } = useGetPoolVehicleLocationList()

  useEffect(() => {
    const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId || 0
    const empLocationIsExist = locationList?.map((o: any) => o.id).includes(employeeLocation)
    setLocationIds(() => (empLocationIsExist ? [employeeLocation] : []))
    setValue('locationIds', empLocationIsExist ? [employeeLocation] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationList])

  /* Filter form default values */
  const defaultValues: IPoolVehicleListReq = {
    locationIds: locationIds,
    reservationDate: TODAY,
    gearTypeId: 0,
    fuelTypeIds: [],
  }

  /*  Search and filter from Pool Vehicle list */
  const [searchText, setSearchText] = useState('')
  const [filteredList, setFilteredList] = useState<IFilteredPoolVehicleList[] | []>([])

  /* Filter form state  */
  const { control, reset, handleSubmit, formState, getValues, setValue, watch } = useForm<IPoolVehicleListReq>({
    defaultValues,
  })
  const filteredDate = watch('reservationDate') || TODAY

  /* Get Pool Vehicle List - Locations | Custom Hooks */
  const { getPoolVehiclesList, vehicles, loading } = useGetPoolVehicleList()

  /* Form Actions | Methods | button disabled states */
  const { isDirty, submitCount } = formState
  const disableResetBtn = !isDirty && submitCount < 1
  const disableSubmitBtn = !isDirty && submitCount < 1

  /* Handle Form Submit */
  const onSubmit = (data: IPoolVehicleListReq) => getPoolVehiclesList(data)
  const runAfterSubmit = () => getPoolVehiclesList(getValues())

  /* Handle Reset Filter Form */
  const handleResetForm = () => {
    reset(
      { locationIds: locationIds, reservationDate: TODAY },
      { keepDirtyValues: false, keepDirty: false, keepDefaultValues: false }
    )
    !!submitCount && onSubmit(defaultValues)
    setSearchText('')
  }

  /* Get Pool Vehicle List on initial page load  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useDidMountEffect(() => onSubmit(defaultValues), [isLoading])

  /* Render PoolVehicles or filtered Pool Vehicle list */
  const renderList = searchText ? filteredList : vehicles

  const {
    modal: isOpen,
    openModal,
    closeModal,
    notification,
    setNotification,
    selectedVehicle,
  } = usePoolVehicleResModal()

  const closeNotification = () => {
    runAfterSubmit()
    setNotification(false)
  }

  /* Show full-screen loader only on initial load (when list is empty).
     During background refresh after a reservation, keep cards/notification mounted. */
  if ((loading || isLoading) && !vehicles.length) return <ListLoader />

  return (
    <div className="campus-services">
      {/* Filter Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLayoutWrapper>
          <PoolVehicleSearch
            vehicles={vehicles}
            setData={setFilteredList}
            searchText={searchText}
            setSearchText={setSearchText}
          />

          <PoolVehicleFilterForm
            control={control}
            locationList={locationList}
            gearTypes={gearTypes}
            fuelTypes={fuelTypes}
          />
        </FormLayoutWrapper>

        {/* Action Buttons */}
        <div className="d-flex flex-1 justify-content-end gap-3 w-100 mb-3">
          <ResetButton
            id="poolVehicleResetBtn"
            disabled={disableResetBtn}
            type="button"
            onClick={handleResetForm}
            label={t('most-used-words.reset')}
            className="p-button-outlined"
          />

          <FilterButton
            id="poolVehicleFilterBtn"
            disabled={disableSubmitBtn}
            type="submit"
            label={t('most-used-words.filter')}
          />
        </div>
      </form>

      <Divider />

      {/* Card List — keep mounted during refresh so the success notification
          dialog (rendered below) is not unmounted. */}
      <RoomCardList>
        {!!renderList?.length ? (
          renderList?.map((vehicle) => <PoolVehicleCard openModal={openModal} key={vehicle.id} vehicle={vehicle} />)
        ) : (
          <NoContent />
        )}
      </RoomCardList>

      {/* Pool Vehicle Reservation Scheduler Modal */}
      {!!selectedVehicle ? (
        <ReservationModalWrapper
          open={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          showNotification={() => {
            runAfterSubmit()
            setNotification(true)
          }}
          selectedVehicle={selectedVehicle}
          filterDate={filteredDate}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('pool-vehicle-res-modal.notification-title')}
        content={t('pool-vehicle-res-modal.notification-text')}
        firstButtonLabel={t('buttons.go-to-calendar')}
        onHide={closeNotification}
        onSubmit={() => history.push('/calendar')}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </div>
  )
}

function ListLoader() {
  return (
    <div className="py-5 my-4">
      <Loader src={calendarLoading} />
    </div>
  )
}
